import axios from 'axios';
import { translate, getLang } from '../i18n/LanguageContext';
import { API_URL } from './api';

// Istanza axios INDIPENDENTE da services/api.js: un agente (staff) e un
// cliente potrebbero avere entrambe le sessioni aperte nello stesso browser
// (tabs diverse), quindi token/redirect vanno tenuti separati — stessa
// interceptor di api.js ma su chiavi localStorage e route di login diverse.
const customerApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

customerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('customer_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

customerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isPublicOrAuthEndpoint =
      url.includes('/public/tickets') ||
      url.includes('/customer/register') ||
      url.includes('/customer/confirm-email') ||
      url.includes('/customer/login');

    if (error.response?.status === 401 && !isPublicOrAuthEndpoint) {
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer_user');
      window.location.href = '/portal/login';
    }
    return Promise.reject(error);
  }
);

export const errorMessage = (err, fallback) => {
  const def = translate('errors.communication', getLang());
  return err?.response?.data?.message || err?.response?.data?.error || fallback || def;
};

// --- Portale pubblico (nessuna autenticazione) ---

export const submitPublicTicket = async (data) => {
  // FormData quando il form ha allegati (multipart, richiesto per gli
  // UploadedFileInterface lato PHP) - Content-Type va lasciato indefinito
  // così axios/il browser aggiungono il boundary multipart da soli, invece
  // di ereditare l'application/json di default dell'istanza.
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
  const response = await customerApi.post('/public/tickets', data, isFormData ? { headers: { 'Content-Type': undefined } } : undefined);
  return response.data;
};

export const getPublicTicket = async (token) => {
  const response = await customerApi.get(`/public/tickets/${token}`);
  return response.data;
};

export const addPublicTicketComment = async (token, body) => {
  const response = await customerApi.post(`/public/tickets/${token}/comments`, { body });
  return response.data;
};

export const getPublicRating = async (token) => {
  const response = await customerApi.get(`/public/tickets/${token}/rating`);
  return response.data;
};

export const submitPublicRating = async (token, rating, comment) => {
  const response = await customerApi.post(`/public/tickets/${token}/rating`, { rating, comment });
  return response.data;
};

export const getPublicTicketProject = async (slug) => {
  const response = await customerApi.get(`/public/ticket-projects/${slug}`);
  return response.data;
};

export const getPublicProjectFields = async (slug) => {
  const response = await customerApi.get(`/public/ticket-projects/${slug}/fields`);
  return response.data;
};

// --- Cliente autenticato ---

export const registerCustomer = async (data) => {
  const response = await customerApi.post('/customer/register', data);
  return response.data;
};

export const confirmCustomerEmail = async (customerId, otp) => {
  const response = await customerApi.post('/customer/confirm-email', { customer_id: customerId, otp });
  return response.data;
};

export const loginCustomer = async (projectSlug, email, password) => {
  const response = await customerApi.post('/customer/login', { project_slug: projectSlug, email, password });
  return response.data;
};

export const getCustomerTickets = async () => {
  const response = await customerApi.get('/customer/tickets');
  return response.data;
};

export const getCustomerProjects = async () => {
  const response = await customerApi.get('/customer/projects');
  return response.data;
};

export const getCustomerTicket = async (id) => {
  const response = await customerApi.get(`/customer/tickets/${id}`);
  return response.data;
};

export const createCustomerTicket = async (data) => {
  const response = await customerApi.post('/customer/tickets', data);
  return response.data;
};

export const addCustomerTicketComment = async (id, body) => {
  const response = await customerApi.post(`/customer/tickets/${id}/comments`, { body });
  return response.data;
};

export default customerApi;
